import React, { useState, useMemo } from 'react';
import { TrendingUp, ChevronDown } from 'lucide-react';
import { NgapAct } from '../types/ngap';
import { POINT_VALUE_METROPOLE, POINT_VALUE_DROM, formatTarif } from '../utils/pricingUtils';

interface RevalorizationSectionProps {
  act: NgapAct;
  darkMode: boolean;
  defaultExpanded?: boolean;
}

interface RevaloEntry {
  date: Date;
  label: string;
  shortLabel: string;
  isActive: boolean;
  gainPoints: number;
  newValue: string;
  previousValue: number;
}

const RevalorizationSection: React.FC<RevalorizationSectionProps> = ({ act, darkMode, defaultExpanded = false }) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const revalorisations = useMemo<RevaloEntry[]>(() => {
    const now = new Date();
    const baseValue = act.old_cotation_valeur
      ? parseFloat(act.old_cotation_valeur.replace(',', '.'))
      : parseFloat(act.cotation_valeur.replace(',', '.'));

    const entries: RevaloEntry[] = [];
    let previousValue = baseValue || 0;

    const add = (
      flag: boolean | undefined,
      value: string | undefined,
      isoDate: string,
      label: string,
      shortLabel: string
    ) => {
      if (!flag || !value) return;
      const newValue = parseFloat(value.replace(',', '.')) || 0;
      const gainPoints = newValue - previousValue;
      entries.push({
        date: new Date(isoDate),
        label,
        shortLabel,
        isActive: now >= new Date(isoDate),
        gainPoints,
        newValue: value,
        previousValue
      });
      previousValue = newValue;
    };

    add(act.revalo_july_2025, act.cotation_valeur_july_2025, '2025-07-01', '1er juillet 2025', 'Juil. 2025');
    add(act.revalo_janv_2026, act.cotation_valeur_janv_2026, '2026-01-01', '1er janvier 2026', 'Janv. 2026');
    add(act.revalo_may_2026, act.cotation_valeur_may_2026, '2026-05-28', '28 mai 2026', 'Mai 2026');
    add(act.revalo_sept_2026, act.cotation_valeur_sept_2026, '2026-09-01', '1er septembre 2026', 'Sept. 2026');
    add(act.revalo_july_2027, act.cotation_valeur_july_2027, '2027-07-01', '1er juillet 2027', 'Juil. 2027');

    return entries;
  }, [act]);

  if (revalorisations.length === 0) return null;

  const totalGainMetropole = revalorisations.reduce(
    (sum, r) => sum + r.gainPoints * POINT_VALUE_METROPOLE,
    0
  );

  const mutedText = darkMode ? 'text-appines-white/45' : 'text-appines-petrol/65';
  const bodyText = darkMode ? 'text-appines-white/65' : 'text-appines-petrol/65';
  const borderColor = darkMode ? 'border-appines-white/12' : 'border-[rgba(1,42,54,0.12)]';

  return (
    <div className={`mt-3 rounded-md border ${borderColor}`}>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`w-full flex items-center justify-between px-3 py-2 text-sm transition-colors ${
          darkMode ? 'hover:bg-appines-white/5' : 'hover:bg-[rgba(1,42,54,0.04)]'
        }`}
        aria-expanded={isExpanded}
      >
        <div className="flex items-center gap-2 min-w-0">
          <TrendingUp className="h-4 w-4 text-appines-emerald flex-shrink-0" aria-hidden="true" />
          <span className={`font-medium ${darkMode ? 'text-appines-white/80' : 'text-appines-petrol'}`}>
            Revalorisations ({revalorisations.length})
          </span>
          {!isExpanded && (
            <span className={`text-xs truncate ${mutedText}`}>
              · +{formatTarif(totalGainMetropole)} € au total
            </span>
          )}
        </div>
        <ChevronDown
          className={`h-4 w-4 ${mutedText} flex-shrink-0 transition-transform ${
            isExpanded ? 'rotate-180' : ''
          }`}
        />
      </button>

      {isExpanded && (
        <div className={`border-t ${borderColor}`}>
          <table className="w-full text-sm">
            <thead>
              <tr className={`text-xs uppercase tracking-wide ${mutedText}`}>
                <th className="text-left font-medium px-3 py-2">Date</th>
                <th className="text-right font-medium px-2 py-2">Points</th>
                <th className="text-right font-medium px-2 py-2">Métropole</th>
                <th className="text-right font-medium px-3 py-2">DROM</th>
              </tr>
            </thead>
            <tbody>
              {revalorisations.map((revalo) => {
                const newValueNum = parseFloat(revalo.newValue.replace(',', '.'));
                const tarifMetropole = newValueNum * POINT_VALUE_METROPOLE;
                const tarifDrom = newValueNum * POINT_VALUE_DROM;
                const gainMetropole = revalo.gainPoints * POINT_VALUE_METROPOLE;
                const gainDrom = revalo.gainPoints * POINT_VALUE_DROM;

                const deltaColor =
                  revalo.gainPoints > 0
                    ? 'text-appines-emerald'
                    : revalo.gainPoints < 0
                      ? 'text-[#c04040]'
                      : mutedText;

                return (
                  <tr
                    key={revalo.label}
                    className={`border-t ${borderColor}`}
                  >
                    <td className={`px-3 py-2 ${bodyText}`}>
                      <div className="flex items-center gap-1.5">
                        <span className={darkMode ? 'text-appines-white/80' : 'text-appines-petrol'}>
                          {revalo.shortLabel}
                        </span>
                        {revalo.isActive && (
                          <span className="text-[10px] font-semibold uppercase tracking-wide text-appines-emerald">
                            actif
                          </span>
                        )}
                      </div>
                    </td>
                    <td className={`px-2 py-2 text-right tabular-nums ${bodyText}`}>
                      <span className="font-medium">{revalo.newValue}</span>
                      <span className={`ml-1 text-xs ${deltaColor}`}>
                        ({revalo.gainPoints > 0 ? '+' : ''}{revalo.gainPoints.toFixed(1)})
                      </span>
                    </td>
                    <td className={`px-2 py-2 text-right tabular-nums ${bodyText}`}>
                      <span className="font-medium">{formatTarif(tarifMetropole)} €</span>
                      <span className={`ml-1 text-xs ${deltaColor}`}>
                        ({revalo.gainPoints > 0 ? '+' : ''}{formatTarif(gainMetropole)})
                      </span>
                    </td>
                    <td className={`px-3 py-2 text-right tabular-nums ${bodyText}`}>
                      <span className="font-medium">{formatTarif(tarifDrom)} €</span>
                      <span className={`ml-1 text-xs ${deltaColor}`}>
                        ({revalo.gainPoints > 0 ? '+' : ''}{formatTarif(gainDrom)})
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default RevalorizationSection;
